import { StyleSheet } from 'react-native';

import { webTapFloor } from '../theme';
import type { useTheme } from './theme';

// The stylesheet of FocusPanelInputs, moved out whole when PE-34's axis-aware
// load field pushed that file to 517 lines — one over the 500 hard cap. Same
// house-move the composer already made (composerStyles, loadAxisSelectorStyles,
// coveragePanelStyles): a stylesheet is the one part of a component with no
// behaviour to reason about, so it is the cheapest thing to send next door.
// ⛔ Nothing was renamed or re-grouped on the way: a "tidy while moving" is how
// a pure move stops being verifiable.
export function makeFocusPanelInputsStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // Card grid: 3 equal cells (reps · weight · effort) so each is a 2-unit
    // span of the 6-unit grid the set-count + controls rows share. gap s2 +
    // 44-tall boxes (`input`, `axisOnlyText`) share one rhythm. Antenore
    // 2026-07-02: the value fields looked bigger than the 36 control rows.
    inputRow: {
      flexDirection: 'row',
      gap: s.s2,
      alignItems: 'flex-end',
    },
    field: { flex: 1, gap: 4 },
    // Stands in for the load field on an axis that carries no number. Same
    // box height as an input so the three-cell rhythm holds, but NO border:
    // the dash says "nothing to type here", where a bordered box would say
    // "a field you are not allowed to use".
    axisOnlyText: {
      fontSize: theme.typography.sizes.md,
      color: theme.palette.ink3,
      textAlign: 'center',
      // 44 = `input.height`. Centred with textAlignVertical (native) plus
      // lineHeight = height, which centres the line on RNW too, where
      // textAlignVertical is ignored.
      height: 44,
      lineHeight: 44,
      textAlignVertical: 'center',
    },
    // Label + the AMRAP badge on one line. The cell is a third of the row, so
    // the badge wraps under the label rather than squeezing it on a narrow
    // screen (280dp) — the label must stay readable, the badge can move.
    // minHeight matches the weight column's HeaderTooltip cell (DS-51: its
    // interactive Pressable floors to webTapFloor on web). This label has no
    // Pressable of its own — nothing here needs the floor — but `inputRow`
    // bottom-aligns both columns (`alignItems:'flex-end'`), so without a
    // matching height the reps label would ride below the taller weight
    // label instead of sitting level with it. `alignContent` (not just
    // `alignItems`) is needed too — with `flexWrap` set, react-native-web
    // packs the single wrapped line via `alignContent` (default `stretch`)
    // BEFORE `alignItems` centers items within that line; without it the
    // 12px label sat flush at the row's top instead of centered in the
    // 44px box, an offset the empty AMRAP-badge case never exposed because
    // this row was never taller than its content until this fix (verified
    // headless: 16px offset without `alignContent`, 0px with it).
    fieldLabelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      alignContent: 'center',
      flexWrap: 'wrap',
      gap: 4,
      minHeight: webTapFloor,
    },
    // HeaderTooltip cell for the weight label: left-aligned to match the
    // plain reps/effort labels (overrides HeaderTooltip's default centering).
    labelCell: { alignItems: 'flex-start' },
    fieldLabel: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
      fontWeight: '600',
    },
    input: {
      height: 44,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      paddingHorizontal: s.s3,
      paddingVertical: 0,
      color: p.ink,
      fontSize: ty.sizes.md,
      textAlign: 'center',
      backgroundColor: p.bg,
    },
    // #336: the reps a set asks for, in tabular digits like the badges' figures.
    inputNumeric: { fontVariant: ['tabular-nums'] },
    // Room for the «−» of AssistSignMark (anchored at s3): one em of the input's
    // own size, so at font scale 1.5 a 6-char value stays centred in what is left.
    inputAssist: { paddingLeft: s.s3 + ty.sizes.md },
    inputWrap: { alignSelf: 'stretch' },
    inputNegative: {
      borderColor: p.warn,
      color: p.warn,
    },
    // Inline validation: danger border + a message line below the inputs (never
    // colour alone — reduced-vision a11y).
    inputInvalid: {
      borderColor: p.danger,
      color: p.danger,
    },
    fieldError: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    fieldErrorText: {
      color: p.danger,
      fontSize: ty.sizes.sm,
      fontWeight: '600',
    },
    // The AMRAP "type your reps" cue. `ink2`, not `danger`: it says what to do
    // next, not what went wrong. One tier up from the error text in size (sm,
    // not xs) because it is read standing at a machine and it is the only
    // thing standing between the athlete and a set that will not log.
    fieldCueText: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: '600',
    },
    hint: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
    },
  });
}
